import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Services
import { BroadcastService, ModalService } from '@cv-invision/core';
import { ViewsService } from '../../../../../services/views.service';
import { ExternalApiService } from '../../../../services/external-api.service';
import { LayoutRenderService } from '../../../services/layout-render.service';
import { RulesChangeService } from './services/rules-change.service';
import { Secure } from '@cv-invision/core';
// Models
import {
	DESKTOP_RULE_SET,
	RuleWorkFlow,
	RuleWorkFlowNewRule,
	RuleWorkFlowNewStip,
	RULE_WORKFLOW_SECTION
} from './models/inline-rules.model';
import { RulesEvents } from './models/inline-rules.event';
import { ArraySectionDirectiveModel } from '../../../../models/extended-section-options';
import {
	NEW_RULE_EVENT_FROM_OUTSIDE,
	NEW_STATUS_RULE_EVENT_FROM_OUTSIDE,
	ORDER_STATE,
	RuleModel,
	RULE_MESSAGE_TYPE,
	RULE_STATUS
} from '../../../../../rule-engine/rules/models';
import { RoomRulesDesign, InlineRulesOptions } from '../../models/components.options';
import { LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { PERMISSION } from '../../../../../models/permission.model';
import { RulesService } from '../../../../../rule-engine/rules/services';

@Component({
	selector: 'cv-schema-render-inline-rules',
	templateUrl: './inline-rules.component.html',
	animations: [
		trigger('slideInOut', [
			state('isOpen', style({ height: '450px', 'z-index': '600' })),
			state('isClose', style({ height: '34px', 'min-width': '230px', 'min-height': '0px' })),
			transition('isOpen <=> isClose', animate('180ms'))
		])
	],
	providers: [RulesChangeService]
})
export class InlineRulesComponent implements OnInit, OnDestroy {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();
	options = new InlineRulesOptions();

	displayNotification: boolean;
	notificationMessage: string;
	//isCollapsed = false;
	isCollapsed = [];

	// Custom
	directiveData = new ArraySectionDirectiveModel();
	path: string | null;
	public ruleStatus = RULE_STATUS;
	public ruleWorkflowSection = RULE_WORKFLOW_SECTION;
	// To show hide the current Rule Section being Rendered
	public ruleWorkFlow = new RuleWorkFlow();
	public ruleMessageType = RULE_MESSAGE_TYPE;
	public currentFilters: RULE_MESSAGE_TYPE[] = [
		RULE_MESSAGE_TYPE.Info,
		RULE_MESSAGE_TYPE.Warning,
		RULE_MESSAGE_TYPE.Error
	];
	public ruleSetViolations = [];
	public RULE_STATUS = RULE_STATUS;
	private atComponentDestroy = new Subject();
	selectedItem;
	roomRulesDesign = RoomRulesDesign;

	get animationState(): string {
		let state = '';

		if (this.options.layout === this.roomRulesDesign.INLINE) {
			state = this.ruleWorkFlow.section !== this.ruleWorkflowSection.CLOSED ? 'isOpen' : 'isClose';
		}

		return state;
	}

	get order(): any {
		return this.viewsService.order;
	}

	get roomRulesVisible(): boolean {
		return this.layoutService.extraData.viewRules;
	}

	get activeActions(): boolean {
		return this.layoutService.activateEditableUX;
	}

	get ruleItem(): any {
		return this.rulesChangeService.rulesItems['item_' + this.ruleWorkFlow.selectedRule?.ruleName] || null;
	}

	constructor(
		private externalAPI: ExternalApiService,
		private viewsService: ViewsService,
		private broadcastService: BroadcastService,
		private modalService: ModalService,
		private layoutService: LayoutRenderService,
		private rulesChangeService: RulesChangeService,
		// protected ruleCmp: RulesService
	) {
		// If path exists, wait for the rules to be set and render the new set
		this.viewsService.viewRuleSetChanged
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((ruleSet: any) => {
				this.ruleSetViolations = [];
				this.prepareRuleSetForInlineRules(ruleSet);
			});
	}

	@Secure(PERMISSION.INVISION_DESKTOP_DATA_ANALYST)
	public static hasPermissionAccess(): boolean {
		return true;
	}

	ngOnInit(): void {
		this.options = { ...this.options, ...this.layoutNode.options };
		// The way it is now
		this.path = this.data._Path;

		this.rulesChangeService.setRulesWorkflow(this.ruleWorkFlow);
		this.prepareRuleSetForInlineRules(this.viewsService.viewRuleSet);

		if (this.options.layout === this.roomRulesDesign.FULL_WIDTH) {
			this.changeRuleFlow(this.ruleWorkflowSection.LIST)
		}

		this.rulesChangeService.rulesDataChanged
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event && event.type && this.path == event.path) {
					switch (event.type) {
						case RulesEvents.CHANGE_RULE_FLOW:
							this.changeRuleFlow(event.ruleWorkflowSection, event.ruleViolation)
							break;
						case RulesEvents.UPDATE_STATUS:
							this.onUpdateStatus(event.ruleViolation, event.ruleStatus)
							break;
						case RulesEvents.NEW_RULE:
							this.onCreateNewRule(event.newRule)
							break;
						case RulesEvents.NEW_COMMENT:
							this.onCreateComment(event.newComment)
							break;
						case RulesEvents.NEW_STIP:
							this.onCreateStip(event.newStip)
							break;
						case RulesEvents.DELETE_COMMENT:
							this.onDeleteComment(event.item)
							break;
						case RulesEvents.DELETE_STIP:
							this.onDeleteStip(event.item)
							break;
						default:
							break;
					}
				}
			});
	}

	/**
	 * Compares two section values, basically this was made to avoid a TS compilation error.
	 *
	 * @param value @see RULE_WORKFLOW_SECTION
	 * @param target @see RULE_WORKFLOW_SECTION
	 */
	public compareWorkflowSections(value: RULE_WORKFLOW_SECTION, target: RULE_WORKFLOW_SECTION): boolean {
		return value === target;
	}

	/**
	 * Get the Rule set and Prepare the data
	 * @param rulesSet
	 */
	public prepareRuleSetForInlineRules(ruleSet: any): void {
		if (this.path !== null && ruleSet && ruleSet !== null) {
			// Get only Desktop Rules
			const desktopRuleSet = Object.assign({}, ruleSet);
			if (desktopRuleSet && desktopRuleSet.violations && desktopRuleSet.violations.length > 0) {
				desktopRuleSet.violations.forEach((violation: any) => {
					const violationExists = this.getViolationExist(violation);
					if (violationExists) {
						// Unbid Object to modify
						const rule = Object.assign({}, violation);
						if (rule.notes !== null) {
							const notes = Object.assign([], violation.notes);
							rule.notes = notes;
						}
						if (rule.stips !== null) {
							const stips = Object.assign([], violation.stips);
							rule.stips = stips;
						}
						this.ruleSetViolations.push(rule);
					}
				});
			}
			this.prepareRuleFilters();
		}
	}

	/**
	 * Move to propper private section
	 *
	 * @param violation
	 * @returns true|false
	 */
	private getViolationExist(violation: any): boolean {
		let violationExist = false;

		if ('filter' in this.options) {
			violationExist = this.options.filter(violation, this.path);
		} else {
			violationExist = violation.targets && violation.targets.find((target: any) => {
				return target === this.path;
			});
		}

		return violationExist;
	}

	/**
	 * Set the Rule Filters
	 */
	public prepareRuleFilters(): void {
		// this.ruleWorkFlow.availableRulesOf.Info = 0;
		// this.ruleWorkFlow.availableRulesOf.Warning = 0;
		// this.ruleWorkFlow.availableRulesOf.Error = 0;
		this.ruleWorkFlow.rulesShown = 0;
		this.currentFilters.forEach(item => {
			const numElements = this.ruleSetViolations.filter((rule: any) => rule.messageType === item);
			this.ruleWorkFlow.availableRulesOf[item] = (numElements) ? numElements.length : 0;

			this.ruleWorkFlow.rulesShown += this.ruleWorkFlow.availableRulesOf[item];
		});

		this.currentRuleSetForFilterActivated();
	}

	/**
	 * Determines if current order can create rules
	 */
	public canCreateRule(): boolean {
		// TODO: Review State
		return (
			// this.order.status !== ORDER_STATE.REJECTED &&
			// this.order.status !== ORDER_STATE.APPROVED &&
			this.order.status !== ORDER_STATE.CANCELLED
		);
	}

	/**
	 * On Open Selected Rule
	 * @param ruleWorkflowSection
	 * @param ruleViolation
	 */
	public changeRuleFlow(ruleWorkflowSection: RULE_WORKFLOW_SECTION, ruleViolation: any = null): void {
		//this.isCollapsed = true;
		this.ruleWorkFlow.section = ruleWorkflowSection;
		if (ruleViolation !== null) {
			this.ruleWorkFlow.selectedRule = ruleViolation;
			this.selectedItem = ruleViolation.ruleName;
		}

		// Select the Index of the Current Rule
		if (ruleWorkflowSection === RULE_WORKFLOW_SECTION.DETAIL && ruleViolation !== null) {
			this.ruleWorkFlow.selectedRule.index =
				this.ruleWorkFlow.filteredRuleViolations.findIndex((rule: any) => rule.ruleName === ruleViolation.ruleName);
		}

		if (ruleWorkflowSection === RULE_WORKFLOW_SECTION.LIST && ruleViolation !== null) {
			this.ruleWorkFlow.selectedRule.index =
				this.ruleWorkFlow.filteredRuleViolations.findIndex((rule: any) => rule.ruleName === ruleViolation.ruleName);
		}

		// New Rule
		if (ruleWorkflowSection === RULE_WORKFLOW_SECTION.NEW) {
			this.ruleWorkFlow.newRule.sortId = 0;
			this.ruleWorkFlow.newRule.title = '';
			this.ruleWorkFlow.newRule.description = '';
			this.ruleWorkFlow.newRule.messageType = RULE_MESSAGE_TYPE.Info;
		}
	}

	/**
	 * Go to Previous rule
	 */
	public previousRule(): void {
		let currentIndex = this.ruleWorkFlow.selectedRule.index;
		if (this.ruleWorkFlow.selectedRule.index !== 0) {
			currentIndex--;
			this.ruleWorkFlow.selectedRule = this.ruleWorkFlow.filteredRuleViolations[currentIndex];
			this.ruleWorkFlow.selectedRule.index = currentIndex;
		}
	}

	/**
	 * Go to Next rule
	 */
	public nextRule(): void {
		let currentIndex = this.ruleWorkFlow.selectedRule.index;
		if (this.ruleWorkFlow.selectedRule.index < this.ruleWorkFlow.filteredRuleViolations.length - 1) {
			currentIndex++;
			this.ruleWorkFlow.selectedRule = this.ruleWorkFlow.filteredRuleViolations[currentIndex];
			this.ruleWorkFlow.selectedRule.index = currentIndex;
		}
	}

	/**
	 * Change the type of Rule Message
	 * @param event
	 */
	public onSelectRuleMessageType(event: any): void {
		this.ruleWorkFlow.newRule.messageType = event.name;
	}

	/**
	 * On Filter rules, filter rules by types selected or unselected..
	 * @param type: RULE_MESSAGE_TYPE
	 */
	public onTypeFiltersChange(filter: RULE_MESSAGE_TYPE): void {
		// remove it if already in current filters
		if (filter && this.currentFilters.indexOf(filter) >= 0) {
			this.currentFilters = this.currentFilters.filter(item => item !== filter);
		} else {
			// add it
			this.currentFilters.push(filter);
		}
		this.prepareRuleFilters();
	}

	/**
	 * Returns true if filter is within the current selected filters.
	 *
	 * @param filter @see RULE_MESSAGE_TYPE
	 */
	public containsFilter(filter: RULE_MESSAGE_TYPE): boolean {
		return this.currentFilters.indexOf(filter) >= 0;
	}

	/**
	 * Filter the Rule Set to Match the Option
	 */
	public currentRuleSetForFilterActivated(): void {
		this.ruleWorkFlow.filteredRuleViolations =
			this.ruleSetViolations.filter((ruleViolation: any) => this.currentFilters.includes(ruleViolation.messageType));
	}

	/**
	 * On Select a Stip Change
	 *
	 * @param event
	 * @param stip @see RuleWorkFlowNewStip
	 */
	public onStipSelectedChange(event: any, stip: RuleWorkFlowNewStip): void {
		if (event.target.checked) {
			this.ruleWorkFlow.newStip = Object.assign({}, stip);
		} else {
			this.ruleWorkFlow.newStip = new RuleWorkFlowNewStip();
		}
	}

	/**
	 * On update teh Status of the Rule
	 *
	 * @param ruleViolation @see RuleModel
	 * @param ruleStatus @see RULE_STATUS
	 */
	public async onUpdateStatus(ruleViolation: RuleModel, ruleStatus: RULE_STATUS): Promise<void> {
		// console.log(this.ruleCmp);
		this.ruleWorkFlow.ruleUpdate.name = ruleViolation.ruleName;
		this.ruleWorkFlow.ruleUpdate.status = ruleStatus;
		this.ruleWorkFlow.processing = true;

		const resultRuleUpdate = await this.externalAPI.updateRuleStatus(
			this.viewsService.order.id,
			ruleViolation.ruleSetName,
			ruleViolation.ruleName,
			ruleStatus
		).toPromise();

		if (resultRuleUpdate.succeed) {
			ruleViolation.status = ruleStatus;
			this.ruleWorkFlow.processing = false;

			this.broadcastService.broadcast({
				name: NEW_STATUS_RULE_EVENT_FROM_OUTSIDE,
				event: {
					rule: ruleViolation
				}
			});

		} else {
			this.onError();
		}
	}

	/**
	 * Create a new Note/Comment on the Rule
	 *
	 * @param newComment
	 */
	public async onCreateComment(newComment: string): Promise<void> {
		this.ruleWorkFlow.newComment = newComment;
		this.ruleWorkFlow.processing = true;
		const resultNoteCreated = await this.externalAPI.postRuleNote(
			this.viewsService.order.id,
			this.ruleWorkFlow.selectedRule.ruleSetName,
			this.ruleWorkFlow.selectedRule.ruleName,
			this.ruleWorkFlow.newComment,
			[this.path]
		).toPromise();

		if (resultNoteCreated.succeed) {
			if (!this.ruleWorkFlow.selectedRule.notes || this.ruleWorkFlow.selectedRule.notes === null) {
				this.ruleWorkFlow.selectedRule.notes = [];
			}
			this.ruleWorkFlow.selectedRule.notes.push({
				id: resultNoteCreated.key,
				created: new Date(),
				note: this.ruleWorkFlow.newComment
			});
			this.ruleWorkFlow.newComment = '';
			this.changeRuleFlow(this.ruleWorkflowSection.DETAIL, this.ruleWorkFlow.selectedRule);
			this.ruleWorkFlow.processing = false;
			this.showNotification('Comment Posted');
		} else {
			this.onError();
		}
	}

	/**
	 * Create a new Stip on the Rule
	 *
	 * @param newStip @see RuleWorkFlowNewStip
	 */
	public async onCreateStip(newStip: RuleWorkFlowNewStip): Promise<void> {
		this.ruleWorkFlow.processing = true;
		this.ruleWorkFlow.newStip = newStip;
		const resultStipCreated = await this.externalAPI.postRuleStip(
			this.viewsService.order.id,
			this.ruleWorkFlow.selectedRule.ruleSetName,
			this.ruleWorkFlow.selectedRule.ruleName,
			this.ruleWorkFlow.newStip.stipId,
			this.ruleWorkFlow.newStip.stipulation
		).toPromise();

		if (resultStipCreated.succeed) {
			if (!this.ruleWorkFlow.selectedRule.stips || this.ruleWorkFlow.selectedRule.stips === null) {
				this.ruleWorkFlow.selectedRule.stips = [];
			}
			this.ruleWorkFlow.selectedRule.stips.push({
				id: resultStipCreated.key,
				created: new Date(),
				stipulation: this.ruleWorkFlow.newStip.stipulation,
				note: this.ruleWorkFlow.newComment
			});
			this.ruleWorkFlow.newStip = {
				stipId: null,
				stipulation: ''
			};
			this.changeRuleFlow(this.ruleWorkflowSection.DETAIL);
			this.ruleWorkFlow.processing = false;
			this.showNotification('Stip Posted');
		} else {
			this.onError();
		}
	}

	/**
	 * Delete a comment from the Rule
	 *
	 * @param item
	 */
	public async onDeleteComment(item: any): Promise<void> {
		this.modalService
			.customConfirm(
				'Delete Comment',
				`Do you really want to delete it? There is no undo operation`,
				'icon-warning fas fa-exclamation-circle',
				'Delete',
				'cv-btn-link-error'
			)
			.subscribe(async result => {
				if (result.confirm === 'confirm') {
					this.ruleWorkFlow.processing = true;
					const noteDeleted = await this.externalAPI.deleteRuleNote(
						this.viewsService.order.id,
						this.ruleWorkFlow.selectedRule.ruleSetName,
						this.ruleWorkFlow.selectedRule.ruleName,
						item.id
					).toPromise();

					if (noteDeleted.succeed) {
						const noteToDeleteIndex = this.ruleWorkFlow.selectedRule.notes.findIndex((c: any) => c.id === item.id);
						if (noteToDeleteIndex > -1) {
							this.ruleWorkFlow.selectedRule.notes.splice(noteToDeleteIndex, 1);
						}
						this.ruleWorkFlow.processing = false;
					} else {
						this.onError();
					}
				}
			});
	}

	/**
	 * Delete a comment from the Rule
	 *
	 * @param item
	 */
	public async onDeleteStip(item: any): Promise<void> {
		this.modalService
			.customConfirm(
				'Delete Stip',
				`Do you really want to delete it? There is no undo operation`,
				'icon-warning fas fa-exclamation-circle',
				'Delete',
				'cv-btn-link-error'
			)
			.subscribe(async result => {
				if (result.confirm === 'confirm') {
					this.ruleWorkFlow.processing = true;
					const stipDeleted = await this.externalAPI.deleteStip(
						this.viewsService.order.id,
						this.ruleWorkFlow.selectedRule.ruleSetName,
						this.ruleWorkFlow.selectedRule.ruleName,
						item.id
					).toPromise();

					if (stipDeleted.succeed) {
						const noteToDeleteIndex = this.ruleWorkFlow.selectedRule.stips.findIndex((c: any) => c.id === item.id);
						if (noteToDeleteIndex > -1) {
							this.ruleWorkFlow.selectedRule.stips.splice(noteToDeleteIndex, 1);
						}
						this.ruleWorkFlow.processing = false;
					} else {
						this.onError();
					}
				}
			});
	}

	/**
	 * Create a New Rule
	 *
	 * @param newRule @see RuleWorkFlowNewRule
	 */
	public async onCreateNewRule(newRule: RuleWorkFlowNewRule): Promise<void> {
		this.ruleWorkFlow.processing = true;
		this.ruleWorkFlow.newRule = newRule;
		let nextSortId = null;
		const targets = [];
		if (this.ruleSetViolations && this.ruleSetViolations.length > 0) {
			// Get next Sort Id for the Room
			nextSortId = Math.max(...this.ruleSetViolations.map((rule: any) => rule.sortId));
		}
		this.ruleWorkFlow.newRule.sortId = ((nextSortId && nextSortId !== null) ? nextSortId : 0);

		if (this.options.appendPathToTarget) {
			targets.push(this.path);
		}

		const resultNewRuleCreated = await this.externalAPI.createNewRule(
			this.viewsService.order.id,
			DESKTOP_RULE_SET.DESKTOP, // modify
			this.ruleWorkFlow.newRule,
			targets
		).toPromise();

		if (resultNewRuleCreated.succeed) {
			this.broadcastService.broadcast({
				name: NEW_RULE_EVENT_FROM_OUTSIDE,
				event: {}
			});

			setTimeout(() => {
				this.changeRuleFlow(this.ruleWorkflowSection.LIST);
				this.ruleWorkFlow.processing = false;
				this.showNotification('Rule Created');
			}, 300);
		} else {
			this.onError();
		}
	}

	/**
	 * If an Error occurs, show to the user something goes wrong
	 */
	public onError(): void {
		this.ruleWorkFlow.processing = false;
		this.ruleWorkFlow.ruleUpdate.name = '';
		this.ruleWorkFlow.ruleUpdate.status = null;
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Displays a notification message.
	 * @param message
	 */
	private showNotification(message: string): void {
		this.notificationMessage = message;
		this.displayNotification = true;
		setTimeout(() => {
			this.displayNotification = false;
		}, 2500);
	}
}
